import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bug, AlertTriangle, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_COLORS = {
  low: 'badge-green',
  medium: 'badge-yellow',
  high: 'badge-red',
};
const SEVERITY_LABELS = { low: 'Gering', medium: 'Mittel', high: 'Hoch' };
const TYPE_LABELS = { disease: 'Krankheit', pest: 'Schädling' };

export default function DiseasesPage() {
  const [diseases, setDiseases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/diseases`)
      .then(r => setDiseases(Array.isArray(r.data) ? r.data : []))
      .catch(() => { setDiseases([]); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = diseases.filter(d => {
    const matchType = filter === 'all' || d.type === filter;
    const matchSearch = !search || d.name.toLowerCase().includes(search.toLowerCase()) || d.symptoms.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  return (
    <div className="p-4 md:p-6 space-y-4 animate-fade-in" data-testid="diseases-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-emerald-50">Krankheiten & Schädlinge</h1>
        <p className="text-emerald-100/50 text-sm mt-0.5">Referenz und Behandlungshinweise</p>
      </div>

      {/* Filter/Search */}
      <div className="flex gap-2 flex-wrap">
        <input className="input-grow flex-1 min-w-0 text-sm" placeholder="Suchen..." value={search} onChange={e => setSearch(e.target.value)} data-testid="disease-search" />
        <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #1B2A21' }}>
          {[['all', 'Alle'], ['disease', 'Krankheiten'], ['pest', 'Schädlinge']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)}
              className={`px-3 py-2 text-sm font-medium transition-colors ${filter === val ? 'bg-emerald-500 text-white' : 'text-emerald-100/50 hover:text-emerald-100 hover:bg-[#111C15]'}`}
              data-testid={`filter-${val}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Bug size={48} className="mx-auto text-emerald-500/30 mb-4" />
          <p className="text-emerald-100/50">Keine Einträge gefunden</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="diseases-grid">
          {filtered.map(d => (
            <div key={d.id} className="card-grow p-4" data-testid={`disease-card-${d.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${d.type === 'pest' ? 'text-yellow-400' : 'text-red-400'}`}
                      style={{ background: d.type === 'pest' ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)' }}>
                      {d.type === 'pest' ? <Bug size={16} /> : <AlertTriangle size={16} />}
                    </div>
                    <h3 className="font-heading font-semibold text-emerald-100">{d.name}</h3>
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <span className={`badge ${SEVERITY_COLORS[d.severity] || 'badge-yellow'}`}>
                      {SEVERITY_LABELS[d.severity] || d.severity}
                    </span>
                    <span className="badge badge-blue">{TYPE_LABELS[d.type] || d.type}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-xs text-emerald-100/40 uppercase tracking-wider mb-1">Symptome</p>
                  <p className="text-sm text-emerald-100/70">{d.symptoms}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)' }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Shield size={12} className="text-emerald-400" />
                    <p className="text-xs text-emerald-400 uppercase tracking-wider">Behandlung</p>
                  </div>
                  <p className="text-sm text-emerald-100/70">{d.treatment}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
